import React from 'react';
import { motion } from 'framer-motion';
import { assessmentData, scoreOptions } from '../../data/questions';
import { useAssessment } from '../../context/AssessmentContext';
import { cn } from '../../lib/utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowRight, FiArrowLeft, FiCheck } = FiIcons;

export default function QuestionSection({ sectionIndex, onNext, onPrev, isLast }) {
  const { answers, setAnswers } = useAssessment();
  const section = assessmentData[sectionIndex];

  const handleOptionChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const isSectionComplete = section.questions.every(q => answers[q.id] !== undefined);

  return (
    <motion.div
      key={section.id}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-8"
    >
      <div className="mb-6 border-b border-slate-200 pb-4">
        <h2 className="text-2xl font-bold text-slate-900">{section.title}</h2>
        <p className="text-slate-500 mt-1">Por favor responde honestamente para obtener un resultado preciso.</p>
      </div>

      <div className="space-y-8">
        {section.questions.map((q, idx) => (
          <div key={q.id} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <p className="font-medium text-slate-800 mb-4 text-lg">
              <span className="text-blue-500 mr-2">{idx + 1}.</span>
              {q.text}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {scoreOptions.map(opt => {
                const isSelected = answers[q.id] === opt.value && answers[q.id] !== undefined;
                // Special handling for multiple options having the same value (No / No sé both have 0)
                // We need to store the actual label to differentiate in UI, but value for scoring.
                // Let's store an object { value, label } instead of just value to keep UI consistent.
                const isActuallySelected = answers[q.id]?.label === opt.label;

                return (
                  <button
                    key={opt.label}
                    onClick={() => setAnswers(prev => ({ ...prev, [q.id]: { value: opt.value, label: opt.label } }))}
                    className={cn(
                      "px-4 py-3 rounded-lg border text-sm font-medium transition-all flex items-center justify-center gap-2",
                      isActuallySelected
                        ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
                        : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300"
                    )}
                  >
                    {isActuallySelected && <SafeIcon icon={FiCheck} className="text-blue-600" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-6 border-t border-slate-200 mt-8">
        <button
          onClick={onPrev}
          className="px-6 py-3 text-slate-600 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          Anterior
        </button>
        <button
          onClick={onNext}
          disabled={!isSectionComplete}
          className={cn(
            "px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all shadow-md",
            isSectionComplete
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-slate-200 text-slate-400 cursor-not-allowed"
          )}
        >
          {isLast ? 'Finalizar diagnóstico' : 'Siguiente'}
          {!isLast && <SafeIcon icon={FiArrowRight} />}
          {isLast && <SafeIcon icon={FiCheck} />}
        </button>
      </div>
    </motion.div>
  );
}